import React, { useEffect, useState } from "react";

import {
  TFunction,
  getLanguageFromLocalStorage,
  getNavbarOpenKeys,
  goTo,
  setNavbarOpenKeys,
  triggerTrengo,
  triggerTrengoContactData,
  useAppType,
  useGeneralContext,
  useGetCarbonProgramByFarmId,
  useLocation,
  useNavContext,
  useNavigate,
  useTranslation,
  useUserContext,
} from "utils";

import { DEFAULT_WEBINAR_LINK, LANGUAGE_CODE, SCHEDULE_CALL_LINK } from "consts";

import { ACCOUNTING_METHODOLOGY, CarbonProgram, User } from "models";

import { Flex, Icon, Layout, Link, Menu, Svg, Tag } from "components";

const { Sider } = Layout;

export type MenuItem = {
  children?: MenuItem[];
  icon: React.ReactNode;
  key: string;
  label: string | React.ReactNode;
  link?: string;
  onTitleClick?: (info: MenuClickEvent) => void;
};

type MenuClickEvent = {
  key: string;
  keyPath: string[];
  item: React.ReactInstance;
  domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>;
};

enum KEY_NAMES {
  CONTACT_US = "CONTACT_US",
  COURSES = "COURSES",
  FAQ = "FAQ",
  FARM = "FARM",
  FARMER_TEAM = "FARMER_TEAM",
  FIELD = "FIELD",
  HOME = "HOME",
  KNOWLEDGE = "KNOWLEDGE",
  NEWS_ARTICLES = "NEWS_ARTICLES",
  PAYOUT = "PAYOUT",
  FOOTPRINT = "FOOTPRINT",
  SAVED = "SAVED",
  SEARCH = "SEARCH",
  PLANNING = "PLANNING",
  SIMULATIONS = "SIMULATIONS",
  COVER_CROP_MIXER = "COVER_CROP_MIXER",
  SUPPORT = "SUPPORT",
  WEBINAR = "WEBINAR",
}

const getActiveKey = (pathname: string, search: string = ""): KEY_NAMES | null => {
  if (!pathname) return null;

  if (pathname === "/home") return KEY_NAMES.HOME;

  if (pathname === "/wallet") return KEY_NAMES.PAYOUT;

  if (pathname === "/farm") {
    if (search.includes("activeTab=fields")) return KEY_NAMES.FIELD;
    return KEY_NAMES.FARM;
  }

  if (pathname === "/all-fields/simulations") return KEY_NAMES.SIMULATIONS;
  if (pathname.startsWith("/cmc")) return KEY_NAMES.COVER_CROP_MIXER;

  if (pathname === "/search/knowledge") return KEY_NAMES.KNOWLEDGE;

  if (pathname === "/search") {
    if (search.includes("starred=true")) return KEY_NAMES.SAVED;
    if (search.includes("filter=articles")) return KEY_NAMES.NEWS_ARTICLES;
    if (search.includes("filter=courses")) return KEY_NAMES.COURSES;
    return KEY_NAMES.SEARCH;
  }

  return null;
};

const generateFarmSubMenuItems = ({
  t,
  handleMenuItemClicked,
  carbonProgram,
}: {
  t: TFunction<string[], undefined>;
  handleMenuItemClicked: (e: MenuClickEvent) => void;
  carbonProgram?: CarbonProgram;
}): MenuItem[] => [
  ...(!carbonProgram?.platformFeatures || carbonProgram?.platformFeatures?.fieldManagement?.removalsExperience
    ? [
        {
          icon: <Icon iconName="klim/field" />,
          key: KEY_NAMES.FIELD,
          label: t("railNav.farm.field"),
          link: "all-fields",
          onTitleClick: handleMenuItemClicked,
        },
      ]
    : []),
  ...(carbonProgram?.platformFeatures?.footprinting?.enabled
    ? [
        {
          icon: <Icon iconName="klim/co2-emissions" />,
          key: KEY_NAMES.FOOTPRINT,
          label: t("railNav.farm.footprint"),
          link: "footprinting",
          onTitleClick: handleMenuItemClicked,
        },
      ]
    : []),
  ...(!carbonProgram?.platformFeatures || carbonProgram?.platformFeatures?.fieldManagement?.payouts
    ? [
        {
          icon: <Icon iconName="tabler/wallet" />,
          key: KEY_NAMES.PAYOUT,
          label: t("railNav.farm.payout"),
          link: "wallet",
          onTitleClick: handleMenuItemClicked,
        },
      ]
    : []),
];

export const generateMenuItems = ({
  farm,
  farmSubMenuItems,
  planningSubMenuItems,
  knowledgeSubMenuItems,
  t,
  handleTopLevelItemClicked,
}: {
  farm: User["farm"];
  knowledgeSubMenuItems: MenuItem[];
  farmSubMenuItems: MenuItem[];
  planningSubMenuItems: MenuItem[];
  t: TFunction;
  handleTopLevelItemClicked: (e: MenuClickEvent) => void;
}) => {
  const menuItems = [
    {
      icon: <Icon iconName="tabler/home" />,
      key: KEY_NAMES.HOME,
      label: t("railNav.home"),
      "data-testid": "app-sidebar-home-item",
    }, // doesn't have children, therefore onTitleClick is captured by the Menu component below
    {
      children: farm?.farmOnboarded ? farmSubMenuItems : [],
      icon: <Icon data-testid="app-sidebar-farm-icon" iconName="klim/farm" />,
      key: KEY_NAMES.FARM,
      label: t("railNav.farm.title"),
      link: "all-fields",
      onTitleClick: handleTopLevelItemClicked,
      "data-testid": "app-sidebar-farm-item",
    },
    {
      children: planningSubMenuItems,
      icon: <Icon iconName="klim/panning-crops" />,
      key: KEY_NAMES.PLANNING,
      label: t("railNav.planning.title"),
      onTitleClick: handleTopLevelItemClicked,
      "data-testid": "app-sidebar-planning-item",
    },
    {
      children: knowledgeSubMenuItems,
      icon: <Icon iconName="tabler/news" />,
      key: KEY_NAMES.KNOWLEDGE,
      label: t("railNav.knowledge.title"),
      link: "search/knowledge",
      onTitleClick: handleTopLevelItemClicked,
      "data-testid": "app-sidebar-knowledge-item",
    },
  ];

  if (planningSubMenuItems.length === 0) {
    return menuItems.filter((item) => item.key !== KEY_NAMES.PLANNING);
  }

  return menuItems;
};

export const AppNavSide = () => {
  const { largeDesktop } = useAppType();
  const { appNavSideCollapsed, setAppNavSideCollapsed } = useGeneralContext();
  const { showNavbar }: { showNavbar: boolean } = useNavContext();
  const navigate = useNavigate();
  const { t } = useTranslation("nav");
  const { user } = useUserContext();
  const location = useLocation();

  const { data: carbonProgram } = useGetCarbonProgramByFarmId(user?.farm?.id ?? undefined);

  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [trengo, setTrengo] = useState(false);

  const handleMouseEnter = () => {
    if (largeDesktop) {
      return;
    }

    setAppNavSideCollapsed(false);
  };

  const handleMouseLeave = () => {
    if (largeDesktop) {
      return;
    }
    setAppNavSideCollapsed(true);
  };

  const handleTopLevelItemClicked = (e: MenuClickEvent) => {
    // @ts-expect-error: yes, there is a classList
    const classListAsArray = Array.from(e.domEvent.target.classList);

    // check if the user has clicked on the title or icon
    if (classListAsArray.includes("ant-menu-title-content") || classListAsArray.includes("ant-menu-item-icon")) {
      const menuItem = menuItems.find((item) => item.key === e.key);

      if (menuItem!.link) {
        return navigate(menuItem!.link);
      }
    }

    if (e.key === KEY_NAMES.PLANNING && appNavSideCollapsed) {
      setAppNavSideCollapsed(false);
      setOpenKeys((prevState) => [...prevState, KEY_NAMES.PLANNING]);
      return;
    }

    // otherwise, open the sub-menu
    if (openKeys.includes(e.key)) {
      const newKeys = openKeys.filter((key) => key !== e.key);
      setOpenKeys(newKeys);
      setNavbarOpenKeys(JSON.stringify(newKeys));
    } else {
      const newKeys = [...openKeys, e.key];
      setOpenKeys(newKeys);
      setNavbarOpenKeys(JSON.stringify(newKeys));
    }
  };

  const webinarLink = () => {
    const locale = getLanguageFromLocalStorage();

    if (locale === LANGUAGE_CODE.ZULU) {
      return "https://www.klim.eco/en/farmers/webinars";
    }

    if (locale === LANGUAGE_CODE.ENGLISH) {
      return "https://www.klim.eco/en/farmers/webinars";
    }

    if (locale === LANGUAGE_CODE.POLISH) {
      return "https://www.klim.eco/en/farmers/webinars";
    }

    return DEFAULT_WEBINAR_LINK;
  };

  const contactLink = () => {
    const locale = getLanguageFromLocalStorage();

    if (locale === LANGUAGE_CODE.ZULU) {
      return "https://www.klim.eco/en/farmers/contact";
    }

    if (locale === LANGUAGE_CODE.ENGLISH) {
      return "https://www.klim.eco/en/farmers/contact";
    }

    return SCHEDULE_CALL_LINK;
  };

  const handleMenuItemClicked = (e: MenuClickEvent) => {
    if (!largeDesktop) {
      setAppNavSideCollapsed(true);
    }

    if (e.key === KEY_NAMES.WEBINAR) {
      goTo(e, null, webinarLink(), true);
      return;
    }

    if (e.key === KEY_NAMES.FARMER_TEAM) {
      goTo(e, null, contactLink(), true);
      return;
    }

    if (e.key === KEY_NAMES.HOME) {
      navigate("/home");
      return;
    }

    if (e.key === KEY_NAMES.SIMULATIONS) {
      navigate("/all-fields/simulations");
      return;
    }

    if (e.key === KEY_NAMES.FAQ) {
      triggerTrengoContactData("help_center", trengo, setTrengo, user);
      return;
    }

    if (e.key === KEY_NAMES.CONTACT_US) {
      triggerTrengo("email", trengo, setTrengo, user);
      return;
    }

    const menuItem = allMenuItems.find((item) => item.key === e.key);

    if (menuItem!.link) {
      navigate(menuItem!.link);
    }
  };

  useEffect(() => {
    const previouslyOpenedKeys = getNavbarOpenKeys();
    if (previouslyOpenedKeys) {
      setOpenKeys(JSON.parse(previouslyOpenedKeys));
    }
  }, []);

  useEffect(() => {
    const updatedSelectedKey = getActiveKey(location.pathname, location.search);

    if (selectedKeys.length === 0 && !updatedSelectedKey) return;

    if (!updatedSelectedKey) {
      setSelectedKeys([]);
      return;
    }
    if (updatedSelectedKey !== selectedKeys[0]) {
      setSelectedKeys([updatedSelectedKey]);
    }
  }, [location, selectedKeys]);

  if (!user) {
    return null;
  }

  const { farm } = user as unknown as User;
  const dividerItem = {
    type: "divider",
  };

  const knowledgeSubMenuItems: MenuItem[] = [
    {
      icon: <Icon iconName="tabler/news" />,
      key: KEY_NAMES.NEWS_ARTICLES,
      label: t("railNav.knowledge.newsArticles"),
      link: "search?filter=articles",
      onTitleClick: handleMenuItemClicked,
    },
    {
      icon: <Icon iconName="tabler/school" />,
      key: KEY_NAMES.COURSES,
      label: t("railNav.knowledge.courses"),
      link: "search?filter=courses",
      onTitleClick: handleMenuItemClicked,
    },
    {
      icon: <Icon iconName="tabler/search" />,
      key: KEY_NAMES.SEARCH,
      label: t("railNav.knowledge.search"),
      link: "search",
      onTitleClick: handleMenuItemClicked,
    },
    {
      icon: <Icon iconName="tabler/bookmark" />,
      key: KEY_NAMES.SAVED,
      label: t("railNav.knowledge.saved"),
      link: "search?filter=articles&starred=true",
      onTitleClick: handleMenuItemClicked,
    },
  ];

  const supportSubMenuItems: MenuItem[] = [
    {
      icon: <Icon iconName="tabler/school" />,
      key: KEY_NAMES.WEBINAR,
      label: t("railNav.support.webinar"),
      link: DEFAULT_WEBINAR_LINK,
      onTitleClick: handleMenuItemClicked,
    },
    // @ts-expect-error convert to
    dividerItem,
    {
      icon: <Icon iconName="tabler/users" />,
      key: KEY_NAMES.FARMER_TEAM,
      label: t("railNav.support.farmerTeam"),
      link: SCHEDULE_CALL_LINK,
      onTitleClick: handleMenuItemClicked,
    },
    // @ts-expect-error convert to
    dividerItem,
    {
      icon: <Icon iconName="klim/envelope" />,
      key: KEY_NAMES.CONTACT_US,
      label: t("railNav.support.contactUs"),
      onTitleClick: handleMenuItemClicked,
    },
    // @ts-expect-error convert to
    dividerItem,
    {
      icon: <Icon iconName="tabler/help" />,
      key: KEY_NAMES.FAQ,
      label: t("railNav.support.faq"),
      onTitleClick: handleMenuItemClicked,
    },
  ];

  const hideSimulation = !farm || farm.fields < 1 || !carbonProgram?.isEnabled || carbonProgram.accountingMethodology !== ACCOUNTING_METHODOLOGY.intervention;

  const planningSubMenuItems: MenuItem[] = [
    ...(!carbonProgram?.platformFeatures || carbonProgram?.platformFeatures?.planning?.cropMixComposer
      ? [
          {
            icon: <Icon iconName="klim/plant-ai" />,
            key: KEY_NAMES.COVER_CROP_MIXER,
            label: (
              <>
                {t("railNav.planning.coverCropMixer")}
                <Tag className="margin-left-50" colour="yellow" text={t("new")} />
              </>
            ),
            link: "/cmc",
            onTitleClick: handleMenuItemClicked,
          },
        ]
      : []),
  ];

  if (!hideSimulation) {
    planningSubMenuItems.unshift(
      ...(!carbonProgram?.platformFeatures || carbonProgram?.platformFeatures?.planning?.simulation
        ? [
            {
              icon: <Icon iconName="klim/graph" />,
              key: KEY_NAMES.SIMULATIONS,
              label: t("railNav.planning.simulations"),
              link: "/all-fields/simulations",
              onTitleClick: handleMenuItemClicked,
            },
          ]
        : [])
    );
  }

  const farmSubMenuItems = generateFarmSubMenuItems({ t, handleMenuItemClicked, carbonProgram });
  const menuItems = generateMenuItems({ farm, farmSubMenuItems, planningSubMenuItems, knowledgeSubMenuItems, t, handleTopLevelItemClicked });

  const supportMenuItems: MenuItem[] = [
    {
      key: KEY_NAMES.SUPPORT,
      icon: <Icon iconName="tabler/help" />,
      label: t("railNav.support.title"),
      children: supportSubMenuItems,
    },
  ];

  const subMenuItems = [...farmSubMenuItems, ...planningSubMenuItems, ...knowledgeSubMenuItems, ...supportSubMenuItems];
  const allMenuItems = [...menuItems, ...subMenuItems, ...supportMenuItems];

  return showNavbar ? (
    <>
      <div data-testid="app-sidebar" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <Sider className={`app-sidebar${appNavSideCollapsed ? " collapsed" : ""}`} collapsed={appNavSideCollapsed} width={200}>
          <Flex className="brandmark-container" align="center" justify="space-between">
            {/* I am intentionally loading both icons, and hiding them with CSS as appropriate */}
            {/* If we don't do this, the initial menu open is janky, as the icon is lazy-loaded */}
            <Link className="brandmark-link" to="dashboard">
              <Svg className="brandmark" dataTestId="app-sidebar-brandmark" svgName="icons/klim/brandmark" width={24} height={24} />
              <Svg className="brandmark-with-text" svgName="icons/klim/brandmark-with-text" width={84} height={24} />
            </Link>
            {/* Same here */}
            <Icon
              className="collapse-chevron"
              dataTestId="app-sidebar-collapse-chevron"
              iconName={"tabler/chevrons-right"}
              onClick={() => setAppNavSideCollapsed(false)}
            />
            <Icon
              className="collapse-chevron reverse"
              dataTestId="app-sidebar-collapse-chevron-reverse"
              iconName={"tabler/chevrons-left"}
              onClick={() => setAppNavSideCollapsed(true)}
            />
          </Flex>
          <Flex className="app-sidebar-nav-container" vertical={true} justify="space-between">
            <Menu
              mode="inline"
              defaultOpenKeys={[KEY_NAMES.FIELD]}
              items={menuItems}
              openKeys={appNavSideCollapsed ? [] : openKeys}
              overflowedIndicator={null}
              selectedKeys={selectedKeys}
              // only used for clicks on items with no children, regardless of their position in the menu tree
              onClick={handleMenuItemClicked}
            />
            <Menu mode="vertical" items={supportMenuItems} selectedKeys={selectedKeys} onClick={handleMenuItemClicked} />
          </Flex>
        </Sider>
      </div>
    </>
  ) : null;
};
